'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { X } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Input } from '@/components/ui/input';
import { PromptCard } from './prompt-card';
import { LoaderOverlay } from './LoaderOverlay';

import { Platform, Prompt2 } from '@/types/schema';

const platforms: { label: Platform; value: Platform }[] = [
  { label: 'All', value: 'All' },
  { label: 'Text', value: 'Text' },
  { label: 'Image', value: 'Image' },
];

interface FilterProps {
  category?: string;      
  subcategory?: string;
  tag?:string;  
}

export function Filter({ category, subcategory, tag }: FilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Existing params, defaulting to "All Platforms" + empty search
  const platformParam = searchParams.get('platform') || 'All';
  const searchParam = searchParams.get('search') || '';

  // Local states
  const [search, setSearch] = useState(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt2[]>([]);
  const [loading, setLoading] = useState(false);

  // If there's a non-empty search or a platform != 'All Platforms', we consider it "searching"
  const isSearching = !!debouncedSearch.trim() || platformParam !== 'All';

  // Debounce the search input to limit fetch frequency
  useEffect(() => {
    // If user clears search, skip delay
    if (!search.trim()) {
      setDebouncedSearch('');
      return;
    }
    // Otherwise, wait 500ms
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  /**
   * Builds a new query string from current URL + our custom params.
   * If a param is `null`, we remove it; otherwise we set it.
   */
  const createQueryString = useCallback(
    (params: Record<string, string | null>) => {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      Object.entries(params).forEach(([key, value]) => {
        if (!value) {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, value);
        }
      });
      return newSearchParams.toString();
    },
    [searchParams]
  );

  // We'll store the last known query in a ref. If it hasn't changed, skip updates.
  const prevQueryRef = useRef<{
    search: string | null;
    platform: string | null;
    category: string | null;
    subcategory: string | null;
    tag: string | null;
  }>({
    search: debouncedSearch ? debouncedSearch.trim() : null,
    platform: platformParam !== 'All' ? platformParam : null,
    category: category ?? null,
    subcategory: subcategory ?? null,
    tag: tag ?? null
  });

  // Single effect to (1) update the URL and (2) fetch prompts
  useEffect(() => {
    // Build new params
    const nextQuery = {
      search: debouncedSearch.trim() || null,
      platform: platformParam !== 'All' ? platformParam : null,
      // Include category/subcategory if provided
      category: category ?? null,
      subcategory: subcategory ?? null,
      tag: tag ?? null
    };

    // If the new query is exactly the same as the old one, skip
    if (
      nextQuery.search === prevQueryRef.current.search &&
      nextQuery.platform === prevQueryRef.current.platform &&
      nextQuery.category === prevQueryRef.current.category &&
      nextQuery.subcategory === prevQueryRef.current.subcategory &&
      nextQuery.tag === prevQueryRef.current.tag
    ) {
      return;
    }

    // Otherwise, update the ref
    prevQueryRef.current = nextQuery;

    // 1) Build the query string for the URL (keep platform param for UI state)
    const qsForUrl = createQueryString(nextQuery);
    router.push(`?${qsForUrl}`, { scroll: false });

    // 2) Build the query string for fetch
    // If platform is Text, omit it from the request so backend returns all,
    // then we filter locally to include empty-or-Text.
    const fetchQuery = {
      ...nextQuery,
      platform: platformParam === 'Text' ? null : nextQuery.platform,
    };
    const qsForFetch = createQueryString(fetchQuery);

    // 3) Fetch prompts
    (async () => {
      setLoading(true);
      try {
        // Include limit or any other param you need
        const res = await fetch(`/api/prompts?${qsForFetch}&limit=12`);
        if (!res.ok) throw new Error('Failed to fetch prompts');
        const data = await res.json();
        let results = (data.results || []) as Prompt2[];
        if (platformParam === 'Text') {
          results = results.filter(
            (p: Prompt2) => !p.platform || p.platform === 'Text'
          );
        }
        setFilteredPrompts(results);
      } catch (error) {
        console.error('Error fetching prompts:', error);
        setFilteredPrompts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    debouncedSearch,
    platformParam,
    category,
    subcategory,
    tag,
    router,
    createQueryString,
  ]);

  // Handle platform dropdown change
  const handlePlatformChange = (value: string) => {
    // If user picks 'All Platforms', we pass null
    // Otherwise we set the new platform
    setSearchParams(value === 'All' ? null : value, debouncedSearch.trim() || null);
  };

  // Utility to set both platform and/or search in local state
  function setSearchParams(newPlatformValue: string | null, newSearchValue: string | null) {
    // For demonstration, we only update the URL here.
    router.push(
      `?${createQueryString({
        platform: newPlatformValue,
        search: newSearchValue || null,
        // Keep category/subcategory from props
        category: category ?? null,
        subcategory: subcategory ?? null,
        tag: tag ?? null
      })}`,
      { scroll: false }
    );
  }

  // Clear the local search state
  const clearSearch = () => {
    setSearch('');
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {/* Platform Selector */}
        <div className="w-full md:w-[200px]">
          <Select onValueChange={handlePlatformChange} value={platformParam}>
            <SelectTrigger className="w-full justify-between">
              <SelectValue placeholder="Select Platform" />
            </SelectTrigger>
            <SelectContent>
              {platforms.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Input */}
        <div className="flex-1 relative">
          <Input
            placeholder="Search prompt"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-0 py-[10px] text-slate-600 hover:text-gray-900 focus:outline-none"
              aria-label="Clear search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filtered Results */}
      {isSearching && (
        <div className="mt-2">
          {!loading && (
            filteredPrompts.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredPrompts.map((prompt) => (
                  <PromptCard key={prompt._id} prompt={prompt} />
                ))}
              </div>
            ) : (
              <p className="w-full text-center mt-12 text-slate-600">
                No results found
              </p>
            )
          )}
        </div>
      )}
    </>
  );
}
